import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useMemo } from 'react';

import {
    RecentNeedsMoreInfoSentRow,
    ServerProductName,
} from '@/components/shared/api/endpoints/applicantInformationRequests';
import { useRecentNeedsMoreInfoSentQuery } from '@/components/shared/api/queries/applicant';
import { Pill } from '@/components/shared/components/Pill';

import {
    Container,
    PageTitle,
    ProductList,
    StyledLink,
    TableWrapper,
} from './RecentNeedsMoreInfo.StyledComponents';

const PRODUCT_DISPLAY_NAMES: Record<ServerProductName, string> = {
    Unknown: 'Unknown',
    Factoring: 'Factoring',
    LoadPay: 'LoadPay',
    TcsFuel: 'TCS Fuel Card',
};

export const RecentNeedsMoreInfo = () => {
    const { data: needsMoreInfoData, isLoading, isError, error } = useRecentNeedsMoreInfoSentQuery();

    // Log error for debugging
    if (isError && error) {
        console.warn('RecentNeedsMoreInfo API error:', error);
    }

    const sortedRows = useMemo(() => {
        return [...(needsMoreInfoData ?? [])].sort((a, b) => {
            // Unparseable dates sort last rather than poisoning the comparison with NaN.
            const aTime = new Date(a.dateCreated).getTime();
            const bTime = new Date(b.dateCreated).getTime();
            if (isNaN(aTime)) return isNaN(bTime) ? 0 : 1;
            if (isNaN(bTime)) return -1;
            return bTime - aTime;
        });
    }, [needsMoreInfoData]);

    const dateBodyTemplate = (rowData: RecentNeedsMoreInfoSentRow) => {
        try {
            const d = new Date(rowData.dateCreated);
            if (isNaN(d.getTime())) {
                return rowData.dateCreated;
            }
            return d.toLocaleString(undefined, {
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                second: '2-digit',
                timeZoneName: 'short',
            });
        } catch (err) {
            console.error('Error formatting date:', err);
            return rowData.dateCreated;
        }
    };

    const productsBodyTemplate = (rowData: RecentNeedsMoreInfoSentRow) => {
        return (
            <ProductList>
                {(rowData.products ?? []).map((product) => (
                    <span key={product}>{PRODUCT_DISPLAY_NAMES[product] ?? product}</span>
                ))}
            </ProductList>
        );
    };

    // Inactive only shows for requests closed without a response.
    const statusBodyTemplate = (rowData: RecentNeedsMoreInfoSentRow) => {
        if (!rowData.isPendingApplicantResponse) {
            return <Pill variant="green">Responded</Pill>;
        }
        return rowData.isActive ? (
            <Pill variant="amber">Pending</Pill>
        ) : (
            <Pill variant="red">Inactive</Pill>
        );
    };

    const messageBodyTemplate = (rowData: RecentNeedsMoreInfoSentRow) => {
        return (
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {rowData.moreInfoMessage}
            </div>
        );
    };

    const orpLinkBodyTemplate = (rowData: RecentNeedsMoreInfoSentRow) => {
        return (
            <StyledLink href={`/applicant/${rowData.applicantId}`} target="_blank">
                View Applicant
            </StyledLink>
        );
    };

    const emptyMessage = isError
        ? 'Unable to load data. Please try again later.'
        : 'No information requests found';

    return (
        <Container data-testid="recent-needs-more-info-page">
            <PageTitle>Recent NMI Sent</PageTitle>
            <TableWrapper>
                <DataTable
                    value={sortedRows}
                    tableStyle={{ width: '100%' }}
                    loading={isLoading}
                    emptyMessage={emptyMessage}
                    stripedRows
                    showGridlines={false}
                >
                    <Column
                        field="dateCreated"
                        header="Date Sent"
                        body={dateBodyTemplate}
                        style={{ width: '210px', whiteSpace: 'nowrap' }}
                    ></Column>
                    <Column
                        field="companyName"
                        header="Company Name/DBA"
                        style={{ width: '160px', whiteSpace: 'normal' }}
                    ></Column>
                    <Column
                        field="products"
                        header="Products"
                        body={productsBodyTemplate}
                        style={{ width: '120px' }}
                    ></Column>
                    <Column field="moreInfoType" header="NMI Type" style={{ width: '120px' }}></Column>
                    <Column header="Message" body={messageBodyTemplate}></Column>
                    <Column field="author" header="Author" style={{ width: '140px' }}></Column>
                    <Column
                        field="isActive"
                        header="Status"
                        body={statusBodyTemplate}
                        style={{ width: '110px' }}
                    ></Column>
                    <Column
                        header="ORP Link"
                        body={orpLinkBodyTemplate}
                        style={{ width: '120px' }}
                    ></Column>
                </DataTable>
            </TableWrapper>
        </Container>
    );
};
